import "./Yearly.css"
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import APIURL from "../../common/Config";
import Header from "../header/header";
import Footer from "../footer/footer";

const LibraCard = () => {
  const [horoscopeData, setHoroscopeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const inMemoryCacheRef = useRef(new Map());
  const [activeIndex, setActiveIndex] = useState(null);

  // Static metadata for LIBRA
  const staticMeta = {
    title: "AstroBhagya – Free Libra Yearly Horoscope by Best Astrologer",
    description: "Looking for accurate Libra yearly horoscope predictions 2026? Discover free Libra astrology predictions and annual insights by zodiac sign for a better year ahead.",
    keywords: "Free Libra yearly horoscope predictions 2026, Libra yearly Astrology predictions, Libra annual predictions, Yearly horoscope by Libra zodiac sign",
    author: "Heavenly Expectation Private Limited",
  };

  const toggleAnswer = (index) => {
    setActiveIndex((prev) => (prev === index ? null : index));
  };

  const fetchLibraYearly = useCallback(async (retries = 3) => {
    const dateStr = "20260101"; // 2026 data
    const cacheKey = `libra_yearly_${dateStr}`; 
    setError(null);
    setLoading(true);

    // 1. CHECK LOCALSTORAGE
    const cachedData = localStorage.getItem(cacheKey);
    if (cachedData) {
      try {
        const parsed = JSON.parse(cachedData);
        setHoroscopeData(parsed);
        setLoading(false);
        console.log("✅ Libra Yearly data loaded from localStorage");
        return;
      } catch (e) {
        console.error("Failed to parse cached data:", e);
      }
    }

    // 2. CHECK IN-MEMORY CACHE
    if (inMemoryCacheRef.current.has(cacheKey)) {
      setHoroscopeData(inMemoryCacheRef.current.get(cacheKey));
      setLoading(false);
      return;
    }

    const minLoadingTime = new Promise(resolve => setTimeout(resolve, 500));

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
        console.warn('Fetch request timed out after 15 seconds.');
      }, 15000);

      const response = await fetch(
        `${APIURL}/horroscope/getallprediction?type=yearly&startDate=${dateStr}&endDate=${dateStr}&language=english`,
        { signal: controller.signal }
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (Array.isArray(data)) {
        // Find LIBRA data (id: 36 or sunShine contains "LIBRA") 
        const libraData = data.find(
          item => item.id === 36 || item.sunShine?.toUpperCase().includes("LIBRA")
        );

        if (libraData) {
          // Cache the data
          inMemoryCacheRef.current.set(cacheKey, libraData);
          localStorage.setItem(cacheKey, JSON.stringify(libraData));
          setHoroscopeData(libraData);
        } else {
          throw new Error("Libra data not found in API response");
        }
      } else {
        throw new Error("Unexpected response format from API");
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        setError('The network request timed out. Please check your internet connection and try again.');
        console.error("Network timeout error:", error);
      } else {
        setError(`Failed to load yearly horoscope: ${error.message}. Please try again.`);
        console.error("Error fetching Libra yearly data:", error);
      }

      if (retries > 0) {
        console.log(`Retrying fetch for Libra Yearly. Retries left: ${retries - 1}`);
        await new Promise(resolve => setTimeout(resolve, 3000));
        return fetchLibraYearly(retries - 1);
      } else {
        // FALLBACK DATA - LIBRA 2026 
        console.log("All retries exhausted. Using fallback data.");
        const fallbackData = {
          sunShine: "Libra 2026: Your year, as the stars align",
          prediction: `<div class="horoscope">
            <p>2026 brings Libra transformation through balance. Patient planning and strategic decisions create stability and growth.</p>
            <h2>Love and Relationships</h2>
            <p>Deep emotional intimacy strengthens bonds. Meaningful connections built on trust and mutual respect.</p>
            <h2>Career and Finances</h2>
            <p>Leadership opportunities reward diplomacy. Careful budgeting ensures financial security.</p>
            <h2>Health & Wellness</h2>
            <p>Balanced lifestyle prevents stress. Meditation and yoga maintain emotional equilibrium.</p>
            <h2>Astrological Remedies</h2>
            <ul>
              <li><strong>Wear Lapis Lazuli:</strong> Enhances clarity and communication.</li>
              <li><strong>Spiritual Practice:</strong> Meditation maintains inner peace.</li>
              <li><strong>Venus Remedies:</strong> Friday white flowers for love/finance.</li>
            </ul>
          </div>`
        };
        setHoroscopeData(fallbackData);
      }
    } finally {
      await minLoadingTime;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLibraYearly();
  }, [fetchLibraYearly]);

  // Set meta tags
  useEffect(() => {
    document.title = staticMeta.title;

    const setOrCreateMetaTag = (name, content) => {
      let metaTag = document.querySelector(`meta[name="${name}"]`);
      if (metaTag) {
        metaTag.setAttribute("content", content);
      } else {
        metaTag = document.createElement("meta");
        metaTag.setAttribute("name", name);
        metaTag.setAttribute("content", content);
        document.head.appendChild(metaTag);
      }
    };

    setOrCreateMetaTag("description", staticMeta.description);
    setOrCreateMetaTag("keywords", staticMeta.keywords);
    setOrCreateMetaTag("author", staticMeta.author);
    setOrCreateMetaTag("revisit-after", "7 days");
    setOrCreateMetaTag("distribution", "global");
    setOrCreateMetaTag("rating", "general");

    let charsetMeta = document.querySelector("meta[charset]");
    if (!charsetMeta) {
      charsetMeta = document.createElement("meta");
      charsetMeta.setAttribute("charset", "UTF-8");
      document.head.prepend(charsetMeta);
    }
  }, []);

  if (loading) {
    return (
      <div className="loader-container">
        <div className="spinner" />
        <p>Loading Libra 2026 Horoscope...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p>{error}</p>
        <button className="retry-button" onClick={() => fetchLibraYearly()}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <div>
        <section id="Make">
          <div className="year-container">
            <h4 className="mt-3 mb-3 text-center font-weight-bold">
              {horoscopeData?.sunShine || "Libra 2026: Your year, as the stars align"}
            </h4>
            <div className="icons-wrapper text-center mb-4">
              <img
                src={require("../../assets/Libraaa.webp")}
                alt="Libra"
                className="horoscope-icon mx-2"
              />
            </div>

            {horoscopeData?.prediction && (
              <main>
                <div
                  dangerouslySetInnerHTML={{
                    __html: horoscopeData.prediction
                  }}
                />
              </main>
            )}

            <div className="btn-wrapper">
              <Link to="/yearly-horoscope/virgo" className="btn btn-customs">
                <i className="bi bi-arrow-left"></i> Virgo Horoscope 2026
              </Link>
              <Link to="/yearly-horoscope/scorpio" className="btn btn-customs">
                <i className="bi bi-arrow-right"></i> Scorpio Horoscope 2026
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ SECTION - SAME AS PREVIOUS */}
        <div className="faq-section">
          <div className="container-item">
            <h3>Frequently Asked Questions (FAQs) – Libra Yearly Horoscope</h3>
            <div className="fitem">
              {[
                {
                  question: "1. What are the core personality traits of Libra?",
                  answer: "Libra individuals are known for their diplomacy, charm, balance-seeking nature, and strong sense of justice. Ruled by Venus, they excel in relationships and harmony.",
                },
                {
                  question: "2. What can Libra expect in love and relationships in 2026?",
                  answer: "2026 deepens emotional intimacy. Trust and open communication strengthen bonds. Meaningful connections develop gradually.",
                },
                {
                  question: "3. What does 2026 look like for Libra career growth?",
                  answer: "Leadership opportunities reward diplomatic skills. Strategic planning brings professional recognition and stability.",
                },
                {
                  question: "4. What health advice for Libra in 2026?",
                  answer: "Balanced lifestyle prevents stress. Regular exercise, yoga, and meditation maintain physical/emotional health.",
                },
                {
                  question: "5. Key astrological remedies for Libra?",
                  answer: "Lapis Lazuli stone, meditation practice, and Friday Venus remedies enhance clarity and harmony.",
                },
              ].map((item, index) => (
                <div className="faq-item" key={index}>
                  <div
                    className={`faq-question ${
                      activeIndex === index ? "active" : ""
                    }`}
                    onClick={() => toggleAnswer(index)}
                  >
                    {item.question}
                  </div>
                  <div
                    className={`faq-answer ${
                      activeIndex === index ? "show" : ""
                    }`}
                  >
                    {item.answer}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default LibraCard; 
